<script setup>
import { ref, computed, nextTick, onUnmounted } from 'vue'
import HeaderPanel from '@/components/HeaderPanel.vue'
import HtmlEditorWorkbench from '@/components/html-editor/HtmlEditorWorkbench.vue'
import {
  assignNodeIds,
  buildDomTreeFromDocument,
  findElementByNodeId,
  getParentNodeIds,
  collectExpandedKeys,
  duplicateElementByNodeId,
  moveElementByTreeDrop,
} from '@/composables/useHtmlEditorDom'
import { useHtmlEditorUndo } from '@/composables/useHtmlEditorUndo'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()
const { canUndo, clearUndoStack, pushBeforeMutation, pushUndoSnapshot, undo: undoEditor } = useHtmlEditorUndo()

const apiBase = 'https://ncht.bz'

/** Лимит исходного .html при загрузке в редактор */
const MAX_HTML_FILE_BYTES = 12 * 1024 * 1024
/** Лимит сырого HTML при отправке на PDF (multipart, без JSON-раздувания) */
const MAX_HTML_UPLOAD_BYTES = 95 * 1024 * 1024

const selectedFile = ref(null)
const originalHtml = ref('')
const initialHtml = ref('')
const textBlocks = ref([])
const editorMode = ref(null)
const editorWorkbenchRef = ref(null)
const editorFrameSrcdoc = ref('')
const domTreeNodes = ref([])
const selectedTreeKey = ref('')
const expandedTreeKeys = ref({})
const structuralBodyHtml = ref('')
const editorModeOptions = [
  { label: 'Обычный', value: 'normal' },
  { label: 'A4-страницы', value: 'paged' },
]
const selectedElementLabel = ref('')
const displayMode = ref('block')
const widthPx = ref('')
const heightPx = ref('')
const fontSizePx = ref('')
const opacityPercent = ref('100')
const borderRadius = ref('0')
const backgroundColor = ref('#ffffff')
const textColor = ref('#111111')
const textAlignMode = ref('none')
const elementAlignMode = ref('none')
const borderStyle = ref('solid')
const marginTop = ref('0')
const marginRight = ref('0')
const marginBottom = ref('0')
const marginLeft = ref('0')
const paddingTop = ref('0')
const paddingRight = ref('0')
const paddingBottom = ref('0')
const paddingLeft = ref('0')
const borderTopWidth = ref('0')
const borderRightWidth = ref('0')
const borderBottomWidth = ref('0')
const borderLeftWidth = ref('0')
const borderTopColor = ref('#111111')
const borderRightColor = ref('#111111')
const borderBottomColor = ref('#111111')
const borderLeftColor = ref('#111111')
const imageSourceMode = ref('upload')
const imageUrlInput = ref('')
const imageAltInput = ref('')
const imageWidthPx = ref('')
const imageHeightPx = ref('')
const pendingImageSrc = ref('')
const imageFileName = ref('')
const loading = ref(false)
const error = ref('')

const fileInput = ref(null)
const formatContextMenuItems = ref([])
let selectedEditorElement = null
let savedSelectionRange = null
let frameHandlers = null
let frameHandlerDoc = null
let textEditUndoNodeId = null

function resetTextEditUndoSession() {
  textEditUndoNodeId = null
}

function clearEditorUndoStack() {
  clearUndoStack()
  resetTextEditUndoSession()
}

function captureEditorSnapshot() {
  const doc = getEditorDoc()
  if (!doc?.body) return null
  let bodyHtml
  if (editorMode.value === 'paged' && structuralBodyHtml.value) {
    bodyHtml = structuralBodyHtml.value
  } else {
    const clone = doc.cloneNode(true)
    sanitizeDocumentForEditor(clone)
    bodyHtml = clone.body.innerHTML
  }
  const selectedNodeId = selectedEditorElement?.getAttribute?.('data-ncht-node-id') || selectedTreeKey.value || null
  return {
    bodyHtml,
    selectedNodeId: selectedNodeId ? String(selectedNodeId) : null,
    editorMode: editorMode.value,
  }
}

function pushUndoImmediate() {
  pushBeforeMutation(captureEditorSnapshot, { immediate: true })
}

function pushUndoForTextInput(event) {
  const inputType = String(event?.inputType || '')
  if (inputType === 'historyUndo' || inputType === 'historyRedo') return
  const editEl = selectedEditorElement || resolvePickTarget(event?.target)
  const nodeId = editEl?.getAttribute?.('data-ncht-node-id')
  if (!nodeId) return
  if (String(nodeId) === textEditUndoNodeId) return
  pushUndoImmediate()
  textEditUndoNodeId = String(nodeId)
}

function pushUndoDebounced() {
  pushBeforeMutation(captureEditorSnapshot)
}

async function restoreEditorSnapshot(snapshot) {
  const doc = getEditorDoc()
  if (!doc?.body || !snapshot) return
  resetPaletteSelection()
  resetTextEditUndoSession()
  doc.body.innerHTML = snapshot.bodyHtml
  structuralBodyHtml.value = snapshot.bodyHtml
  ensureLiveDocumentNodeIds(doc)
  markEditableInFrameDocument(doc)
  if (editorMode.value === 'paged') {
    paginatePreviewFrameDocument(doc)
    ensureLiveDocumentNodeIds(doc)
    markEditableInFrameDocument(doc)
  }
  rebuildDomTree(true)
  if (snapshot.selectedNodeId) {
    selectElementByNodeId(snapshot.selectedNodeId)
  }
  ensureFrameHandlersAttached()
}

async function handleUndo() {
  await undoEditor(restoreEditorSnapshot)
}

function detachFrameHandlers() {
  if (!frameHandlerDoc || !frameHandlers) return
  const capture = frameHandlers.capture === true
  frameHandlerDoc.removeEventListener('mousedown', frameHandlers.pick, capture)
  frameHandlerDoc.removeEventListener('contextmenu', frameHandlers.contextmenu, capture)
  frameHandlerDoc.removeEventListener('beforeinput', frameHandlers.beforeinput, capture)
  frameHandlerDoc.removeEventListener('input', frameHandlers.input, capture)
  frameHandlers = null
  frameHandlerDoc = null
}

function ensureLiveDocumentNodeIds(doc) {
  if (!doc?.body) return
  assignNodeIds(doc.body, true)
}

function capturePagedPreviewStructuralState(doc) {
  if (!doc?.body) return
  const root = doc.body.querySelector('[data-ncht-preview-root]')
  if (!root) {
    captureStructuralState(doc)
    return
  }
  ensureLiveDocumentNodeIds(doc)
  const chunks = []
  root.querySelectorAll('.ncht-preview-content').forEach((content) => {
    Array.from(content.children).forEach((child) => {
      chunks.push(child.outerHTML)
    })
  })
  structuralBodyHtml.value = chunks.join('')
}

function syncSelectedElementFromTree() {
  const doc = getEditorDoc()
  const nodeId = selectedTreeKey.value || selectedEditorElement?.getAttribute?.('data-ncht-node-id') || ''
  if (!doc) {
    resetPaletteSelection()
    return
  }
  if (selectedEditorElement && !doc.contains(selectedEditorElement)) {
    selectedEditorElement = null
  }
  if (!nodeId) return
  const el = findElementByNodeId(doc, nodeId)
  if (el) {
    selectElement(el)
  } else {
    resetPaletteSelection()
  }
}

function attachFrameHandlersToDoc(iframe, doc) {
  if (!iframe || !doc) return
  detachFrameHandlers()

  const handlePick = (event) => {
    if (event.button != null && event.button !== 0) return
    getFormatContextMenuRef()?.hide?.()
    const element = resolvePickTarget(event.target)
    if (!element) return
    selectElement(element)
  }
  const handleContextMenu = (event) => {
    getFormatContextMenuRef()?.hide?.()
    const element = resolvePickTarget(event.target)
    if (!element) return
    selectElement(element)
    const hasTextSelection = !!getSelectedTextFromEditor()
    if (hasTextSelection) saveEditorSelectionRange()
    else savedSelectionRange = null
    formatContextMenuItems.value = buildContextMenuItems(hasTextSelection)
    event.preventDefault()
    const frameRect = iframe.getBoundingClientRect()
    const menuEvent = {
      pageX: frameRect.left + event.clientX + window.scrollX,
      pageY: frameRect.top + event.clientY + window.scrollY,
      clientX: frameRect.left + event.clientX,
      clientY: frameRect.top + event.clientY,
      target: iframe,
      currentTarget: iframe,
      preventDefault: () => {},
      stopPropagation: () => {},
    }
    window.requestAnimationFrame(() => {
      getFormatContextMenuRef()?.show?.(menuEvent)
    })
  }
  const handleBeforeInput = (event) => {
    pushUndoForTextInput(event)
  }
  const handleInput = () => {
    scheduleDomTreeRebuild()
  }

  const capture = true
  frameHandlers = {
    pick: handlePick,
    contextmenu: handleContextMenu,
    beforeinput: handleBeforeInput,
    input: handleInput,
    capture,
  }
  frameHandlerDoc = doc
  doc.addEventListener('mousedown', handlePick, capture)
  doc.addEventListener('contextmenu', handleContextMenu, capture)
  doc.addEventListener('beforeinput', handleBeforeInput, capture)
  doc.addEventListener('input', handleInput, capture)
}

function ensureFrameHandlersAttached() {
  const iframe = getEditorFrameRef()
  const doc = getEditorDoc()
  if (!iframe || !doc) return
  if (!frameHandlers || frameHandlerDoc !== doc) {
    attachFrameHandlersToDoc(iframe, doc)
  }
}

function buildContextMenuItems(hasTextSelection) {
  return [
    { label: 'Копировать', icon: 'pi pi-copy', command: () => handleCopyFromContext(hasTextSelection) },
    { label: 'Вырезать', icon: 'pi pi-minus', disabled: !hasTextSelection, command: () => handleCutFromContext() },
    { label: 'Вставить', icon: 'pi pi-clone', disabled: !hasTextSelection, command: () => handlePasteFromContext() },
    { separator: true },
    {
      label: 'Форматирование',
      icon: 'pi pi-pencil',
      items: [
        { label: 'Жирный', icon: 'pi pi-bold', disabled: !hasTextSelection, command: () => applyInlineFormat('bold') },
        { label: 'Курсив', icon: 'pi pi-italic', disabled: !hasTextSelection, command: () => applyInlineFormat('italic') },
        {
          label: 'Подчеркнутый',
          icon: 'pi pi-underline',
          disabled: !hasTextSelection,
          command: () => applyInlineFormat('underline'),
        },
        {
          label: 'Зачеркнутый',
          icon: 'pi pi-strikethrough',
          disabled: !hasTextSelection,
          command: () => applyInlineFormat('strikeThrough'),
        },
        { label: 'Цитата', icon: 'pi pi-comment', disabled: !hasTextSelection, command: () => applyBlockQuote() },
        { label: 'Гиперссылка', icon: 'pi pi-link', disabled: !hasTextSelection, command: () => applyHyperlink() },
      ],
    },
    {
      label: 'Вставка изображения',
      icon: 'pi pi-image',
      items: [
        { label: 'Из файла', icon: 'pi pi-upload', disabled: hasTextSelection, command: () => insertImageFromFileViaContext() },
        { label: 'По ссылке (URL)', icon: 'pi pi-link', disabled: hasTextSelection, command: () => insertImageFromUrlViaContext() },
      ],
    },
  ]
}

const fileName = computed(() =>
  selectedFile.value ? selectedFile.value.name : 'Файл не выбран'
)

const fileSizeHuman = computed(() => {
  if (!selectedFile.value) return ''
  const size = selectedFile.value.size
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`
  return `${(size / 1024 / 1024).toFixed(1)} МБ`
})
const hasEditableBlocks = computed(() => textBlocks.value.length > 0)
const isEditorOpen = computed(() => editorMode.value !== null)

function unwrapExposedRef(exposed) {
  if (exposed == null) return null
  if (typeof exposed === 'object' && Object.prototype.hasOwnProperty.call(exposed, 'value')) {
    return exposed.value ?? null
  }
  return exposed
}

function getEditorFrameRef() {
  return unwrapExposedRef(editorWorkbenchRef.value?.editorFrameRef)
}

function getFormatContextMenuRef() {
  return unwrapExposedRef(editorWorkbenchRef.value?.formatContextMenuRef)
}

function getImageFileInputRef() {
  return unwrapExposedRef(editorWorkbenchRef.value?.imageFileInput)
}

function isSkippableTextNode(node) {
  if (!node) return true
  if (!String(node.nodeValue || '').trim()) return true
  let parent = node.parentNode
  while (parent && parent.nodeType === 1) {
    const tag = String(parent.nodeName || '').toLowerCase()
    if (tag === 'script' || tag === 'style' || tag === 'noscript' || tag === 'template') return true
    parent = parent.parentNode
  }
  return false
}

function nodeParentPath(node) {
  const parts = []
  let el = node?.parentElement || null
  while (el && el.nodeType === 1 && parts.length < 5) {
    const tag = String(el.tagName || '').toLowerCase()
    if (tag && tag !== 'html' && tag !== 'body') parts.push(tag)
    el = el.parentElement
  }
  if (!parts.length) return 'body'
  return parts.reverse().join(' > ')
}

function collectTextNodes(rootNode) {
  const textNodes = []
  const walker = document.createTreeWalker(rootNode, 4)
  let node = walker.nextNode()
  while (node) {
    if (!isSkippableTextNode(node)) textNodes.push(node)
    node = walker.nextNode()
  }
  return textNodes
}

function extractTextBlocksFromHtml(html) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const nodes = collectTextNodes(doc.body)
  return nodes.map((node, index) => ({
    id: index + 1,
    path: nodeParentPath(node),
    text: String(node.nodeValue || ''),
  }))
}

function sanitizeDocumentForEditor(doc) {
  if (!doc) return
  doc.querySelectorAll('[data-ncht-editor-style]').forEach((n) => n.remove())
  doc.querySelectorAll('[data-ncht-selected]').forEach((n) => n.removeAttribute('data-ncht-selected'))
  doc.querySelectorAll('[data-ncht-editable]').forEach((n) => {
    n.removeAttribute('data-ncht-editable')
    n.removeAttribute('contenteditable')
  })
  doc.querySelectorAll('[id="monica-writing-entry-btn-root"]').forEach((n) => n.remove())
  doc.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes || []).forEach((attr) => {
      if (String(attr.name || '').startsWith('data-listener-added_')) {
        el.removeAttribute(attr.name)
      }
    })
    if (el.style) {
      const outlineValue = String(el.style.outline || '').toLowerCase()
      const boxShadowValue = String(el.style.boxShadow || '').toLowerCase()
      const isEditorOutline =
        outlineValue.includes('rgba(59, 130, 246') ||
        outlineValue.includes('rgba(34, 197, 94') ||
        outlineValue.includes('rgb(59, 130, 246)') ||
        outlineValue.includes('rgb(34, 197, 94)')
      const isEditorShadow =
        boxShadowValue.includes('rgba(34, 197, 94') ||
        boxShadowValue.includes('rgb(34, 197, 94)')
      if (isEditorOutline) {
        el.style.removeProperty('outline')
        el.style.removeProperty('outline-offset')
      }
      if (isEditorShadow) {
        el.style.removeProperty('box-shadow')
      }
      if (!String(el.getAttribute('style') || '').trim()) {
        el.removeAttribute('style')
      }
    }
  })
}

function serializeDocumentWithDoctype(doc) {
  const doctype = doc.doctype
    ? `<!DOCTYPE ${doc.doctype.name}${doc.doctype.publicId ? ` PUBLIC "${doc.doctype.publicId}"` : ''}${
        doc.doctype.systemId ? ` "${doc.doctype.systemId}"` : ''
      }>\n`
    : ''
  return `${doctype}${doc.documentElement.outerHTML}`
}

function rgbToHex(color) {
  if (!color) return '#000000'
  const normalized = String(color).trim().toLowerCase()
  if (normalized === 'transparent') return ''
  if (normalized.startsWith('#')) {
    if (normalized.length === 4) {
      return `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`
    }
    return normalized
  }
  const rgba = normalized.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\)/)
  if (rgba && rgba[4] != null && Number(rgba[4]) === 0) return ''
  const match = rgba
  if (!match) return '#000000'
  const toHex = (n) => Number(n).toString(16).padStart(2, '0')
  return `#${toHex(match[1])}${toHex(match[2])}${toHex(match[3])}`
}

function isTransparentColor(color) {
  const normalized = String(color || '').trim().toLowerCase()
  if (!normalized) return true
  if (normalized === 'transparent') return true
  return /rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0(?:\.0+)?\s*\)/.test(normalized)
}

function resolveEffectiveBackgroundHex(el, view) {
  let node = el
  while (node && node.nodeType === 1) {
    const style = view?.getComputedStyle ? view.getComputedStyle(node) : null
    const bg = String(style?.backgroundColor || '')
    if (!isTransparentColor(bg)) {
      return rgbToHex(bg) || '#ffffff'
    }
    node = node.parentElement
  }
  return '#ffffff'
}

function parsePx(value, fallback = 0) {
  const n = Number.parseFloat(String(value || '').replace('px', ''))
  if (!Number.isFinite(n)) return fallback
  return n
}

function stripPx(value) {
  if (!value || value === 'auto') return ''
  return String(Math.max(0, Math.round(parsePx(value, 0))))
}

function resetPaletteSelection() {
  const previous = selectedEditorElement
  if (previous?.removeAttribute) previous.removeAttribute('data-ncht-selected')
  selectedEditorElement = null
  selectedElementLabel.value = ''
  selectedTreeKey.value = ''
  resetTextEditUndoSession()
}

function syncPaletteFromElement(el) {
  const iframe = getEditorFrameRef()
  const view = iframe?.contentWindow
  if (!el || !view) return
  const style = view.getComputedStyle(el)
  selectedElementLabel.value = `<${String(el.tagName || '').toLowerCase()}>`
  displayMode.value = style.display || 'block'
  widthPx.value = stripPx(style.width)
  heightPx.value = stripPx(style.height)
  fontSizePx.value = stripPx(style.fontSize)
  opacityPercent.value = String(Math.round((Number(style.opacity || 1) || 1) * 100))
  marginTop.value = stripPx(style.marginTop)
  marginRight.value = stripPx(style.marginRight)
  marginBottom.value = stripPx(style.marginBottom)
  marginLeft.value = stripPx(style.marginLeft)
  const marginLeftRaw = String(style.marginLeft || '').toLowerCase()
  const marginRightRaw = String(style.marginRight || '').toLowerCase()
  if (marginLeftRaw === 'auto' && marginRightRaw === 'auto') elementAlignMode.value = 'center'
  else if (marginLeftRaw === 'auto') elementAlignMode.value = 'right'
  else if (marginRightRaw === 'auto') elementAlignMode.value = 'left'
  else elementAlignMode.value = 'none'
  paddingTop.value = stripPx(style.paddingTop)
  paddingRight.value = stripPx(style.paddingRight)
  paddingBottom.value = stripPx(style.paddingBottom)
  paddingLeft.value = stripPx(style.paddingLeft)
  borderTopWidth.value = stripPx(style.borderTopWidth)
  borderRightWidth.value = stripPx(style.borderRightWidth)
  borderBottomWidth.value = stripPx(style.borderBottomWidth)
  borderLeftWidth.value = stripPx(style.borderLeftWidth)
  borderStyle.value = style.borderTopStyle || 'none'
  borderTopColor.value = rgbToHex(style.borderTopColor || '#111111')
  borderRightColor.value = rgbToHex(style.borderRightColor || '#111111')
  borderBottomColor.value = rgbToHex(style.borderBottomColor || '#111111')
  borderLeftColor.value = rgbToHex(style.borderLeftColor || '#111111')
  backgroundColor.value = resolveEffectiveBackgroundHex(el, view)
  textColor.value = rgbToHex(style.color || '#111111')
  textAlignMode.value = el.style.textAlign || 'none'
  borderRadius.value = String(Math.max(0, Math.round(parsePx(style.borderTopLeftRadius, 0))))
}

function selectElement(el) {
  if (!el) return
  const nextNodeId = el.getAttribute('data-ncht-node-id') || ''
  const prevNodeId = selectedEditorElement?.getAttribute?.('data-ncht-node-id') || ''
  if (nextNodeId !== prevNodeId) resetTextEditUndoSession()
  if (selectedEditorElement?.removeAttribute) selectedEditorElement.removeAttribute('data-ncht-selected')
  selectedEditorElement = el
  selectedEditorElement.setAttribute('data-ncht-selected', '1')
  syncPaletteFromElement(el)
  const nodeId = el.getAttribute('data-ncht-node-id')
  if (nodeId) {
    selectedTreeKey.value = nodeId
    expandedTreeKeys.value = {
      ...expandedTreeKeys.value,
      ...collectExpandedKeys(getParentNodeIds(el)),
    }
  }
}

function selectElementByNodeId(nodeId) {
  const doc = getEditorDoc()
  const el = findElementByNodeId(doc, nodeId)
  if (!el) return
  selectElement(el)
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

function onTreeSelect(nodeId) {
  selectElementByNodeId(nodeId)
}

function parseStructuralBodyDocument(bodyHtml) {
  const parser = new DOMParser()
  return parser.parseFromString(
    `<!DOCTYPE html><html><head></head><body>${bodyHtml || ''}</body></html>`,
    'text/html'
  )
}

function captureStructuralState(doc) {
  if (!doc?.body) return
  if (doc.body.querySelector('[data-ncht-preview-root]')) return
  assignNodeIds(doc.body, true)
  const clone = doc.cloneNode(true)
  sanitizeDocumentForEditor(clone)
  structuralBodyHtml.value = clone.body.innerHTML
}

function getStructureDocument() {
  if (editorMode.value === 'paged' && structuralBodyHtml.value) {
    return parseStructuralBodyDocument(structuralBodyHtml.value)
  }
  return getEditorDoc()
}

function commitStructureDocument(structureDoc) {
  if (!structureDoc?.body) return
  const prevNodeId = selectedTreeKey.value || selectedEditorElement?.getAttribute?.('data-ncht-node-id') || ''
  assignNodeIds(structureDoc.body, true)
  const clone = structureDoc.cloneNode(true)
  sanitizeDocumentForEditor(clone)
  structuralBodyHtml.value = clone.body.innerHTML

  const liveDoc = getEditorDoc()
  if (!liveDoc?.body) return

  liveDoc.body.innerHTML = structuralBodyHtml.value
  if (editorMode.value === 'paged') {
    paginatePreviewFrameDocument(liveDoc)
  }
  ensureLiveDocumentNodeIds(liveDoc)
  markEditableInFrameDocument(liveDoc)
  rebuildDomTree(true)
  if (prevNodeId) {
    selectElementByNodeId(prevNodeId)
  }
  ensureFrameHandlersAttached()

  if (editorMode.value !== 'paged') {
    sanitizeDocumentForEditor(liveDoc)
    originalHtml.value = serializeDocumentWithDoctype(liveDoc)
    textBlocks.value = extractTextBlocksFromHtml(originalHtml.value)
  }
}

function onTreeContextAction({ nodeId, action }) {
  const doc = getStructureDocument()
  if (!doc || !nodeId) return
  if (action === 'duplicate') {
    pushUndoImmediate()
    const clone = duplicateElementByNodeId(doc, nodeId)
    if (!clone) return
    const cloneNodeId = `dup-${Date.now()}`
    clone.setAttribute('data-ncht-node-id', cloneNodeId)
    commitStructureDocument(doc)
    selectElementByNodeId(cloneNodeId)
    return
  }
  if (action === 'delete') {
    const el = findElementByNodeId(doc, nodeId)
    if (!el) return
    pushUndoImmediate()
    resetPaletteSelection()
    el.remove()
    commitStructureDocument(doc)
  }
}

function onTreeNodeDrop(event) {
  const doc = getStructureDocument()
  if (!doc) return
  const before = captureEditorSnapshot()
  const moved = moveElementByTreeDrop(doc, event)
  if (!moved) {
    rebuildDomTree(true)
    return
  }
  const movedNodeId = moved.getAttribute('data-ncht-node-id') || `mv-${Date.now()}`
  if (!moved.getAttribute('data-ncht-node-id')) {
    moved.setAttribute('data-ncht-node-id', movedNodeId)
  }
  pushUndoSnapshot(before)
  commitStructureDocument(doc)
  selectElementByNodeId(movedNodeId)
}

function rebuildDomTree(preserveExistingIds = false) {
  const doc = getEditorDoc()
  if (!doc) {
    domTreeNodes.value = []
    return
  }

  let treeDoc = doc
  if (editorMode.value === 'paged' && structuralBodyHtml.value) {
    ensureLiveDocumentNodeIds(doc)
    treeDoc = parseStructuralBodyDocument(structuralBodyHtml.value)
    preserveExistingIds = true
  } else {
    captureStructuralState(doc)
  }

  domTreeNodes.value = buildDomTreeFromDocument(treeDoc, preserveExistingIds)
  if (!preserveExistingIds && domTreeNodes.value.length) {
    const nextExpanded = { ...expandedTreeKeys.value }
    for (const node of domTreeNodes.value) {
      nextExpanded[node.key] = true
    }
    expandedTreeKeys.value = nextExpanded
  }
  syncSelectedElementFromTree()
  ensureFrameHandlersAttached()
}

function applyLayoutStyles() {
  if (!selectedEditorElement) return
  pushUndoDebounced()
  selectedEditorElement.style.display = displayMode.value || 'block'
  selectedEditorElement.style.width = widthPx.value ? `${Math.max(0, Number(widthPx.value) || 0)}px` : ''
  selectedEditorElement.style.height = heightPx.value ? `${Math.max(0, Number(heightPx.value) || 0)}px` : ''
  selectedEditorElement.style.fontSize = fontSizePx.value ? `${Math.max(1, Number(fontSizePx.value) || 0)}px` : ''
}

function applySpacingStyles() {
  if (!selectedEditorElement) return
  pushUndoDebounced()
  selectedEditorElement.style.marginTop = `${Math.max(0, Number(marginTop.value) || 0)}px`
  selectedEditorElement.style.marginRight = `${Math.max(0, Number(marginRight.value) || 0)}px`
  selectedEditorElement.style.marginBottom = `${Math.max(0, Number(marginBottom.value) || 0)}px`
  selectedEditorElement.style.marginLeft = `${Math.max(0, Number(marginLeft.value) || 0)}px`
  selectedEditorElement.style.paddingTop = `${Math.max(0, Number(paddingTop.value) || 0)}px`
  selectedEditorElement.style.paddingRight = `${Math.max(0, Number(paddingRight.value) || 0)}px`
  selectedEditorElement.style.paddingBottom = `${Math.max(0, Number(paddingBottom.value) || 0)}px`
  selectedEditorElement.style.paddingLeft = `${Math.max(0, Number(paddingLeft.value) || 0)}px`
}

function applyElementHorizontalAlignStyles() {
  if (!selectedEditorElement) return
  pushUndoDebounced()
  const mode = elementAlignMode.value || 'none'
  if (mode === 'none') {
    selectedEditorElement.style.removeProperty('margin-left')
    selectedEditorElement.style.removeProperty('margin-right')
    return
  }
  const tag = String(selectedEditorElement.tagName || '').toLowerCase()
  if (tag === 'img') {
    selectedEditorElement.style.display = 'block'
  }
  if (mode === 'left') {
    selectedEditorElement.style.marginLeft = '0px'
    selectedEditorElement.style.marginRight = 'auto'
  } else if (mode === 'center') {
    selectedEditorElement.style.marginLeft = 'auto'
    selectedEditorElement.style.marginRight = 'auto'
  } else if (mode === 'right') {
    selectedEditorElement.style.marginLeft = 'auto'
    selectedEditorElement.style.marginRight = '0px'
  }
}

function setElementAlignMode(mode) {
  elementAlignMode.value = mode
  applyElementHorizontalAlignStyles()
}

function applyAppearanceStyles() {
  if (!selectedEditorElement) return
  pushUndoDebounced()
  const normalizedBorderStyle = String(borderStyle.value || 'none').toLowerCase()
  const topWidth = Math.max(0, Number(borderTopWidth.value) || 0)
  const rightWidth = Math.max(0, Number(borderRightWidth.value) || 0)
  const bottomWidth = Math.max(0, Number(borderBottomWidth.value) || 0)
  const leftWidth = Math.max(0, Number(borderLeftWidth.value) || 0)
  const hasVisibleBorderWidths = topWidth > 0 || rightWidth > 0 || bottomWidth > 0 || leftWidth > 0
  const hasVisibleBorderStyle = normalizedBorderStyle !== 'none' && normalizedBorderStyle !== 'hidden'

  if (!hasVisibleBorderWidths || !hasVisibleBorderStyle) {
    selectedEditorElement.style.removeProperty('border')
    selectedEditorElement.style.removeProperty('border-style')
    selectedEditorElement.style.removeProperty('border-top-width')
    selectedEditorElement.style.removeProperty('border-right-width')
    selectedEditorElement.style.removeProperty('border-bottom-width')
    selectedEditorElement.style.removeProperty('border-left-width')
    selectedEditorElement.style.removeProperty('border-top-color')
    selectedEditorElement.style.removeProperty('border-right-color')
    selectedEditorElement.style.removeProperty('border-bottom-color')
    selectedEditorElement.style.removeProperty('border-left-color')
  } else {
    selectedEditorElement.style.borderStyle = normalizedBorderStyle
    selectedEditorElement.style.borderTopWidth = `${topWidth}px`
    selectedEditorElement.style.borderRightWidth = `${rightWidth}px`
    selectedEditorElement.style.borderBottomWidth = `${bottomWidth}px`
    selectedEditorElement.style.borderLeftWidth = `${leftWidth}px`
    selectedEditorElement.style.borderTopColor = borderTopColor.value
    selectedEditorElement.style.borderRightColor = borderRightColor.value
    selectedEditorElement.style.borderBottomColor = borderBottomColor.value
    selectedEditorElement.style.borderLeftColor = borderLeftColor.value
  }
  selectedEditorElement.style.borderRadius = `${Math.max(0, Number(borderRadius.value) || 0)}px`
  selectedEditorElement.style.opacity = `${Math.max(0, Math.min(1, (Number(opacityPercent.value) || 100) / 100))}`
  selectedEditorElement.style.backgroundColor = backgroundColor.value
  selectedEditorElement.style.color = textColor.value
  if (textAlignMode.value === 'none') {
    selectedEditorElement.style.removeProperty('text-align')
  } else {
    selectedEditorElement.style.textAlign = textAlignMode.value || 'left'
  }
}

function setTextAlignMode(mode) {
  textAlignMode.value = mode
  applyAppearanceStyles()
}

function removeSelectedElement() {
  if (!selectedEditorElement) return
  const nodeId = selectedEditorElement.getAttribute('data-ncht-node-id')
  const structureDoc = getStructureDocument()
  if (nodeId && structureDoc) {
    const el = findElementByNodeId(structureDoc, nodeId)
    if (el) {
      pushUndoImmediate()
      resetPaletteSelection()
      el.remove()
      commitStructureDocument(structureDoc)
      return
    }
  }
  pushUndoImmediate()
  const toDelete = selectedEditorElement
  resetPaletteSelection()
  toDelete.remove()
  rebuildDomTree(true)
}

function getEditorDoc() {
  return getEditorFrameRef()?.contentDocument || null
}

function getSelectedTextFromEditor() {
  const doc = getEditorDoc()
  const sel = doc?.getSelection ? doc.getSelection() : null
  return String(sel?.toString() || '').trim()
}

async function writeTextToClipboard(value) {
  const text = String(value || '')
  if (!text.trim()) return false
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fallback below
  }
  const doc = getEditorDoc()
  if (!doc) return false
  const textarea = doc.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', 'readonly')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  doc.body.appendChild(textarea)
  textarea.select()
  const ok = doc.execCommand('copy')
  doc.body.removeChild(textarea)
  return !!ok
}

async function readTextFromClipboard() {
  try {
    if (navigator?.clipboard?.readText) {
      return await navigator.clipboard.readText()
    }
  } catch {
    // ignore and fallback
  }
  return ''
}

async function handleCopyFromContext(hasTextSelection) {
  const doc = getEditorDoc()
  if (!doc) return
  if (hasTextSelection) {
    const restored = restoreEditorSelectionRange()
    if (!restored) return
    doc.execCommand('copy')
    return
  }
  const textToCopy = String(selectedEditorElement?.innerText || '').trim()
  if (!textToCopy) return
  await writeTextToClipboard(textToCopy)
}

function handleCutFromContext() {
  const doc = getEditorDoc()
  if (!doc) return
  pushUndoImmediate()
  const restored = restoreEditorSelectionRange()
  if (!restored) return
  doc.execCommand('cut')
  savedSelectionRange = null
}

async function handlePasteFromContext() {
  const doc = getEditorDoc()
  if (!doc) return
  pushUndoImmediate()
  const restored = restoreEditorSelectionRange()
  if (!restored) return
  const pastedByBrowser = doc.execCommand('paste')
  if (!pastedByBrowser) {
    const text = await readTextFromClipboard()
    if (text) doc.execCommand('insertText', false, text)
  }
  savedSelectionRange = null
}

function saveEditorSelectionRange() {
  const doc = getEditorDoc()
  const sel = doc?.getSelection ? doc.getSelection() : null
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
    savedSelectionRange = null
    return
  }
  savedSelectionRange = sel.getRangeAt(0).cloneRange()
}

function restoreEditorSelectionRange() {
  const doc = getEditorDoc()
  const win = getEditorFrameRef()?.contentWindow
  if (!doc || !savedSelectionRange || !win?.focus) return false
  win.focus()
  const sel = doc.getSelection ? doc.getSelection() : null
  if (!sel) return false
  sel.removeAllRanges()
  sel.addRange(savedSelectionRange)
  return true
}

function applyInlineFormat(command) {
  const doc = getEditorDoc()
  if (!doc || !restoreEditorSelectionRange()) return
  pushUndoImmediate()
  doc.execCommand(command, false)
  savedSelectionRange = null
}

function applyBlockQuote() {
  const doc = getEditorDoc()
  if (!doc || !restoreEditorSelectionRange()) return
  pushUndoImmediate()
  doc.execCommand('formatBlock', false, 'blockquote')
  savedSelectionRange = null
}

function applyHyperlink() {
  const doc = getEditorDoc()
  if (!doc || !restoreEditorSelectionRange()) return
  const rawUrl = window.prompt('Введите URL ссылки', 'https://')
  if (rawUrl == null) return
  const trimmed = String(rawUrl).trim()
  if (!trimmed) return
  pushUndoImmediate()
  const href = /^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) ? trimmed : `https://${trimmed}`
  doc.execCommand('createLink', false, href)
  const sel = doc.getSelection ? doc.getSelection() : null
  const anchors = new Set()
  if (sel && sel.rangeCount > 0) {
    for (let i = 0; i < sel.rangeCount; i += 1) {
      const range = sel.getRangeAt(i)
      const container =
        range.commonAncestorContainer?.nodeType === 1
          ? range.commonAncestorContainer
          : range.commonAncestorContainer?.parentElement
      if (container?.querySelectorAll) {
        container.querySelectorAll('a').forEach((a) => anchors.add(a))
      }
      const directAnchor = container?.closest ? container.closest('a') : null
      if (directAnchor) anchors.add(directAnchor)
    }
  }
  if (anchors.size === 0 && sel?.anchorNode?.parentElement?.closest) {
    const fallbackAnchor = sel.anchorNode.parentElement.closest('a')
    if (fallbackAnchor) anchors.add(fallbackAnchor)
  }
  anchors.forEach((a) => {
    a.style.backgroundColor = 'inherit'
  })
  savedSelectionRange = null
}

function resetImageForm() {
  imageSourceMode.value = 'upload'
  imageUrlInput.value = ''
  imageAltInput.value = ''
  imageWidthPx.value = ''
  imageHeightPx.value = ''
  pendingImageSrc.value = ''
  imageFileName.value = ''
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Не удалось прочитать изображение'))
    reader.readAsDataURL(file)
  })
}

function isAllowedImageUrl(url) {
  if (!url) return false
  const v = String(url).trim().toLowerCase()
  return v.startsWith('http://') || v.startsWith('https://') || v.startsWith('data:image/')
}

function resolveImageSource() {
  if (imageSourceMode.value === 'url') {
    const src = String(imageUrlInput.value || '').trim()
    if (!src) throw new Error('Укажите URL изображения')
    if (!isAllowedImageUrl(src)) {
      throw new Error('Разрешены только http(s) и data:image URL')
    }
    return src
  }
  const src = String(pendingImageSrc.value || '').trim()
  if (!src) throw new Error('Сначала выберите файл изображения')
  if (!src.startsWith('data:image/')) {
    throw new Error('Загруженный файл должен быть изображением')
  }
  return src
}

async function onImageFileChange(e) {
  const files = e?.target?.files
  const file = files?.[0]
  if (!file) return
  if (!String(file.type || '').startsWith('image/')) {
    error.value = 'Выберите файл изображения'
    pendingImageSrc.value = ''
    imageFileName.value = ''
    return
  }
  try {
    pendingImageSrc.value = await readFileAsDataUrl(file)
    imageFileName.value = file.name || 'image'
    imageSourceMode.value = 'upload'
    imageAltInput.value = ''
    error.value = ''
    insertImageAfterSelected()
  } catch (err) {
    pendingImageSrc.value = ''
    imageFileName.value = ''
    error.value = err?.message || 'Не удалось загрузить изображение'
  }
}

function insertImageFromFileViaContext() {
  if (!selectedEditorElement) {
    error.value = 'Сначала выберите элемент в документе'
    return
  }
  getImageFileInputRef()?.click()
}

function insertImageFromUrlViaContext() {
  if (!selectedEditorElement) {
    error.value = 'Сначала выберите элемент в документе'
    return
  }
  const rawUrl = window.prompt('Введите URL изображения', 'https://')
  if (rawUrl == null) return
  const trimmed = String(rawUrl || '').trim()
  if (!trimmed) return
  imageSourceMode.value = 'url'
  imageUrlInput.value = trimmed
  insertImageAfterSelected()
}

function insertImageAfterSelected() {
  if (!selectedEditorElement) {
    error.value = 'Сначала выберите элемент в документе'
    return
  }
  const iframeDoc = getEditorDoc()
  if (!iframeDoc) {
    error.value = 'Не удалось получить документ редактора'
    return
  }
  try {
    const src = resolveImageSource()
    pushUndoImmediate()
    const img = iframeDoc.createElement('img')
    img.setAttribute('src', src)
    const alt = String(imageAltInput.value || '').trim()
    if (alt) img.setAttribute('alt', alt)

    const width = Number.parseInt(String(imageWidthPx.value || ''), 10)
    const height = Number.parseInt(String(imageHeightPx.value || ''), 10)
    img.style.maxWidth = '100%'
    if (Number.isFinite(width) && width > 0) img.style.width = `${width}px`
    if (Number.isFinite(height) && height > 0) img.style.height = `${height}px`

    selectedEditorElement.insertAdjacentElement('afterend', img)
    ensureLiveDocumentNodeIds(iframeDoc)
    selectElement(img)
    error.value = ''
  } catch (err) {
    error.value = err?.message || 'Не удалось добавить изображение'
  }
}

function stripScriptsFromDocument(doc) {
  if (!doc) return
  doc.querySelectorAll('script').forEach((node) => node.remove())
}

function buildIframeSource(html) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  stripScriptsFromDocument(doc)
  const head = doc.head || doc.getElementsByTagName('head')[0] || doc.documentElement
  const hasBase = !!doc.querySelector('base[href]')
  if (!hasBase && head) {
    const base = doc.createElement('base')
    base.setAttribute('href', `${window.location.origin}/`)
    head.insertBefore(base, head.firstChild)
  }
  const style = doc.createElement('style')
  style.setAttribute('data-ncht-editor-style', 'true')
  style.textContent = `
    [data-ncht-editable="1"] { outline: 1px dashed rgba(59,130,246,.35); outline-offset: 2px; }
    [data-ncht-editable="1"]:focus { outline: 2px solid rgba(59,130,246,.8); }
    [data-ncht-selected="1"] {
      outline: 2px solid rgba(34,197,94,.9) !important;
      outline-offset: 2px;
      box-shadow: 0 0 0 3px rgba(34,197,94,.2) !important;
    }
  `
  head.appendChild(style)
  return serializeDocumentWithDoctype(doc)
}

function buildPagedPreviewSource(html) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  stripScriptsFromDocument(doc)
  const head = doc.head || doc.getElementsByTagName('head')[0] || doc.documentElement
  const hasBase = !!doc.querySelector('base[href]')
  if (!hasBase && head) {
    const base = doc.createElement('base')
    base.setAttribute('href', `${window.location.origin}/`)
    head.insertBefore(base, head.firstChild)
  }
  const style = doc.createElement('style')
  style.setAttribute('data-ncht-editor-style', 'true')
  style.textContent = `
    html, body {
      background: #e5e7eb !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    body {
      padding: 16px 0 24px !important;
    }
    [data-ncht-preview-root] {
      width: 100%;
    }
    .ncht-preview-page {
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto 16px;
      background: #ffffff;
      position: relative;
      box-sizing: border-box;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
      overflow: hidden;
    }
    .ncht-preview-content {
      box-sizing: border-box;
      width: 190mm;
      min-height: 0;
      margin: 10mm;
      overflow: visible;
    }
    [data-ncht-editable="1"] {
      outline: 1px dashed rgba(59,130,246,.35);
      outline-offset: 2px;
    }
    [data-ncht-editable="1"]:focus {
      outline: 2px solid rgba(59,130,246,.8);
    }
  `
  head.appendChild(style)
  return serializeDocumentWithDoctype(doc)
}

function paginatePreviewFrameDocument(doc) {
  if (!doc?.body) return
  const rootAttr = 'data-ncht-preview-root'
  const pageHeightPx = Math.floor((277 * 96) / 25.4)
  const tolerance = 1.5

  const oldRoot = doc.body.querySelector(`[${rootAttr}]`)
  let sourceNodes
  if (oldRoot) {
    sourceNodes = Array.from(oldRoot.querySelectorAll('.ncht-preview-content > *'))
    oldRoot.remove()
  } else {
    sourceNodes = Array.from(doc.body.children).filter((el) => !el.hasAttribute(rootAttr))
  }

  const nodes = sourceNodes.filter((node) => {
    if (node.nodeType === 1 && node.matches && node.matches('script, style')) return false
    if (node.nodeType === 3 && !String(node.nodeValue || '').trim()) return false
    return true
  })

  const view = doc.defaultView
  const getStyle = (el) => (view?.getComputedStyle ? view.getComputedStyle(el) : null)
  const getBreakBefore = (el) => {
    const s = getStyle(el)
    const v = String(s?.breakBefore || s?.pageBreakBefore || 'auto').toLowerCase()
    return v
  }
  const getBreakAfter = (el) => {
    const s = getStyle(el)
    const v = String(s?.breakAfter || s?.pageBreakAfter || 'auto').toLowerCase()
    return v
  }
  const shouldBreakBefore = (el) => {
    const v = getBreakBefore(el)
    return v === 'page' || v === 'always' || v === 'left' || v === 'right'
  }
  const shouldBreakAfter = (el) => {
    const v = getBreakAfter(el)
    return v === 'page' || v === 'always' || v === 'left' || v === 'right'
  }

  const hasMeaningfulDirectText = (el) =>
    Array.from(el.childNodes || []).some((n) => n.nodeType === 3 && String(n.nodeValue || '').trim())

  const canDecomposeNode = (node) => {
    if (!node || node.nodeType !== 1) return false
    if (hasMeaningfulDirectText(node)) return false
    const tag = String(node.tagName || '').toLowerCase()
    if (!['div', 'section', 'article', 'main'].includes(tag)) return false
    const classList = Array.from(node.classList || [])
    const hasUnsafeClass = classList.some((cls) => !['container', 'section', 'section-content'].includes(cls))
    if (hasUnsafeClass) return false
    const isPureStructuralWrapper =
      classList.length > 0 && classList.every((cls) => ['container', 'section', 'section-content'].includes(cls))
    if (!isPureStructuralWrapper && node.id) return false
    if (!isPureStructuralWrapper && node.getAttribute('style')) return false
    const children = Array.from(node.children || []).filter((child) => !child.matches?.('script, style'))
    return children.length >= 2
  }

  const decomposeNode = (node) => {
    if (!canDecomposeNode(node)) return [node]
    return Array.from(node.children || []).filter((child) => !child.matches?.('script, style'))
  }

  const isColoredBox = (node) => {
    if (!node || node.nodeType !== 1 || !node.classList) return false
    return node.classList.contains('box-blue') || node.classList.contains('box-pink')
  }

  const isNestedColoredBox = (node) => {
    if (!isColoredBox(node)) return false
    const parentTag = String(node.parentElement?.tagName || '').toUpperCase()
    return parentTag === 'LI' || parentTag === 'P'
  }

  const getSplittableChildNodes = (parent) =>
    Array.from(parent.childNodes || []).filter((n) => {
      if (n.nodeType === 3) return String(n.nodeValue || '').trim()
      if (n.nodeType === 1) return true
      return false
    })

  const listItemsIn = (listEl) =>
    Array.from(listEl?.children || []).filter((n) => String(n.tagName || '').toLowerCase() === 'li')

  const boxHasSplittableList = (boxNode) =>
    getSplittableChildNodes(boxNode).some((child) => {
      const tag = String(child.tagName || '').toUpperCase()
      return (tag === 'UL' || tag === 'OL') && listItemsIn(child).length > 0
    })

  // sync with backend/modules/html_to_pdf.js splitColoredBoxesForPrint
  const splitBoxNodeForPage = (boxNode, pageContent) => {
    if (!isColoredBox(boxNode) || isNestedColoredBox(boxNode) || !boxHasSplittableList(boxNode)) {
      return null
    }

    const cloneListShell = (listEl) => {
      const tag = String(listEl.tagName || 'ul').toLowerCase()
      const list = boxNode.ownerDocument.createElement(tag)
      Array.from(listEl.attributes || []).forEach((attr) => {
        list.setAttribute(attr.name, attr.value)
      })
      return list
    }

    const collectUnits = () => {
      const units = []
      for (const child of getSplittableChildNodes(boxNode)) {
        const tag = String(child.tagName || '').toUpperCase()
        if ((tag === 'UL' || tag === 'OL') && listItemsIn(child).length > 1) {
          for (const li of listItemsIn(child)) {
            units.push({ kind: 'li', listProto: child, node: li })
          }
        } else {
          units.push({ kind: 'node', node: child })
        }
      }
      return units
    }

    const units = collectUnits()
    if (units.length < 2) return null

    const currentBox = boxNode.cloneNode(false)
    currentBox.removeAttribute('data-ncht-node-id')
    pageContent.appendChild(currentBox)
    const listsByProto = new Map()

    const ensureList = (listProto) => {
      if (listsByProto.has(listProto)) return listsByProto.get(listProto)
      const list = cloneListShell(listProto)
      currentBox.appendChild(list)
      listsByProto.set(listProto, list)
      return list
    }

    const returnUnit = (unit) => {
      if (unit.kind === 'li') {
        ensureList(unit.listProto).appendChild(unit.node)
        return
      }
      const unitIndex = units.findIndex((u) => u.node === unit.node)
      let insertBefore = null
      for (let i = unitIndex + 1; i < units.length; i += 1) {
        const next = units[i].node
        if (next.parentNode === boxNode) {
          insertBefore = next
          break
        }
      }
      if (insertBefore) boxNode.insertBefore(unit.node, insertBefore)
      else boxNode.appendChild(unit.node)
    }

    let fittedCount = 0
    for (const unit of units) {
      if (unit.kind === 'li') {
        ensureList(unit.listProto).appendChild(unit.node)
      } else {
        currentBox.appendChild(unit.node)
      }
      if (pageContent.scrollHeight > pageHeightPx + tolerance) {
        if (unit.kind === 'li') {
          const list = listsByProto.get(unit.listProto)
          if (list?.contains?.(unit.node)) list.removeChild(unit.node)
        } else {
          currentBox.removeChild(unit.node)
        }
        returnUnit(unit)
        break
      }
      fittedCount += 1
    }

    currentBox.querySelectorAll('ul, ol').forEach((list) => {
      if (!list.children.length) list.remove()
    })
    boxNode.querySelectorAll('ul, ol').forEach((list) => {
      if (!list.children.length) list.remove()
    })

    if (fittedCount === 0) {
      currentBox.remove()
      return null
    }

    const remainingChildren = getSplittableChildNodes(boxNode)
    if (!remainingChildren.length) {
      return { currentBoxOnly: true, remainder: null }
    }

    const remainder = boxNode.cloneNode(false)
    remainder.removeAttribute('data-ncht-node-id')
    for (const child of remainingChildren) {
      remainder.appendChild(child)
    }
    boxNode.querySelectorAll('ul, ol').forEach((list) => {
      if (!list.children.length) list.remove()
    })
    return { currentBoxOnly: false, remainder }
  }

  const splitListNodeForPage = (listNode, pageContent) => {
    if (!listNode || listNode.nodeType !== 1) return null
    const tag = String(listNode.tagName || '').toLowerCase()
    if (tag !== 'ul' && tag !== 'ol') return null
    const items = Array.from(listNode.children || []).filter((child) => String(child.tagName || '').toLowerCase() === 'li')
    if (items.length < 2) return null

    const currentList = listNode.cloneNode(false)
    currentList.removeAttribute('data-ncht-node-id')
    pageContent.appendChild(currentList)
    let fittedCount = 0
    for (const item of items) {
      currentList.appendChild(item)
      if (pageContent.scrollHeight > pageHeightPx + tolerance) {
        currentList.removeChild(item)
        break
      }
      fittedCount += 1
    }

    // Ничего не влезло — откатываемся и пусть вызывающий переносит целиком
    if (fittedCount === 0) {
      currentList.remove()
      return null
    }

    const remainingItems = items.slice(fittedCount)
    if (!remainingItems.length) {
      return { currentListOnly: true, remainder: null }
    }

    const remainder = listNode.cloneNode(false)
    remainder.removeAttribute('data-ncht-node-id')
    for (const item of remainingItems) {
      remainder.appendChild(item)
    }
    return { currentListOnly: false, remainder }
  }

  const tryUnwrapSingleWrapper = (list) => {
    let current = list
    for (let i = 0; i < 4; i += 1) {
      if (current.length !== 1) break
      const only = current[0]
      if (!only || only.nodeType !== 1) break
      const tag = String(only.tagName || '').toLowerCase()
      if (!['div', 'main', 'section', 'article'].includes(tag)) break
      if (hasMeaningfulDirectText(only)) break
      const children = Array.from(only.children || []).filter((child) => !child.matches?.('script, style'))
      if (children.length < 2) break
      current = children
    }
    return current
  }

  const splitOversizedNode = (node) => {
    if (!node || node.nodeType !== 1) return [node]
    const rect = node.getBoundingClientRect()
    if (rect.height <= pageHeightPx + tolerance) return [node]
    if (hasMeaningfulDirectText(node)) return [node]
    const children = Array.from(node.children || []).filter((child) => !child.matches?.('script, style'))
    if (children.length < 2) return [node]
    return children
  }

  const root = doc.createElement('div')
  root.setAttribute(rootAttr, '1')
  doc.body.appendChild(root)

  const makePage = () => {
    const page = doc.createElement('div')
    page.className = 'ncht-preview-page'
    const content = doc.createElement('div')
    content.className = 'ncht-preview-content'
    page.appendChild(content)
    root.appendChild(page)
    return content
  }

  let content = makePage()
  const queue = [...tryUnwrapSingleWrapper(nodes)]

  while (queue.length) {
    const node = queue.shift()
    if (!node) continue
    const chunks = splitOversizedNode(node)
    if (chunks.length > 1) {
      queue.unshift(...chunks)
      continue
    }
    const chunk = chunks[0]
    if (chunk.nodeType === 1 && shouldBreakBefore(chunk) && content.childElementCount > 0) {
      content = makePage()
    }
    content.appendChild(chunk)
    if (content.scrollHeight > pageHeightPx + tolerance) {
      content.removeChild(chunk)
      if (isColoredBox(chunk) && boxHasSplittableList(chunk) && !isNestedColoredBox(chunk)) {
        const splitBox = splitBoxNodeForPage(chunk, content)
        if (splitBox) {
          if (splitBox.remainder) queue.unshift(splitBox.remainder)
          if (chunk.nodeType === 1 && shouldBreakAfter(chunk)) content = makePage()
          continue
        }
      }
      const splitList = splitListNodeForPage(chunk, content)
      if (splitList) {
        if (splitList.remainder) queue.unshift(splitList.remainder)
        if (chunk.nodeType === 1 && shouldBreakAfter(chunk)) content = makePage()
        continue
      }
      const decomposed = decomposeNode(chunk)
      if (decomposed.length > 1) {
        queue.unshift(...decomposed)
        continue
      }
      content = makePage()
      content.appendChild(chunk)
    }
    if (chunk.nodeType === 1 && shouldBreakAfter(chunk)) {
      content = makePage()
    }
  }
}

function markEditableInFrameDocument(doc) {
  if (!doc?.body) {
    textBlocks.value = []
    return
  }
  const nodes = collectTextNodes(doc.body)
  const marked = new Set()
  for (const node of nodes) {
    const el = node.parentElement
    if (!el) continue
    const tag = String(el.tagName || '').toLowerCase()
    if (tag === 'html' || tag === 'body') continue
    if (marked.has(el)) continue
    el.setAttribute('contenteditable', 'true')
    el.setAttribute('data-ncht-editable', '1')
    marked.add(el)
  }
  textBlocks.value = nodes.map((node, index) => ({
    id: index + 1,
    path: nodeParentPath(node),
    text: String(node.nodeValue || ''),
  }))
}

function pickHtmlFile(fileList) {
  if (!fileList || !fileList.length) return null
  for (let i = 0; i < fileList.length; i += 1) {
    const f = fileList[i]
    const n = (f.name || '').toLowerCase()
    if (n.endsWith('.html') || n.endsWith('.htm') || f.type === 'text/html') {
      return f
    }
  }
  return fileList[0]
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} КБ`
  return `${(n / 1024 / 1024).toFixed(1)} МБ`
}

function utf8ByteLength(text) {
  return new TextEncoder().encode(String(text || '')).length
}

function pdfFilenameFromHtmlName(name) {
  const base = String(name || 'document').replace(/\.(html?|HTML?)$/i, '')
  return `${base || 'document'}.pdf`
}

function editedHtmlFilenameFromHtmlName(name) {
  const base = String(name || 'document').replace(/\.(html?|HTML?)$/i, '')
  return `${base || 'document'}_edited.html`
}

function triggerDownloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

async function loadHtmlFromFile(file) {
  if (!file) return
  if (file.size > MAX_HTML_FILE_BYTES) {
    throw new Error(`Файл слишком большой (макс. ${Math.round(MAX_HTML_FILE_BYTES / (1024 * 1024))} МБ)`)
  }
  const html = await readFileAsText(file)
  if (!html.trim()) {
    throw new Error('Файл пустой')
  }
  const blocks = extractTextBlocksFromHtml(html)
  if (!blocks.length) {
    throw new Error('В файле нет текстовых блоков для редактирования')
  }
  clearEditorUndoStack()
  selectedFile.value = file
  originalHtml.value = html
  initialHtml.value = html
  textBlocks.value = blocks
  openEditor('normal')
}

function syncHtmlFromIframe() {
  const iframe = getEditorFrameRef()
  const doc = iframe?.contentDocument
  if (!doc) return false
  if (editorMode.value === 'paged') return false
  sanitizeDocumentForEditor(doc)
  structuralBodyHtml.value = doc.body.innerHTML
  const updatedHtml = serializeDocumentWithDoctype(doc)
  originalHtml.value = updatedHtml
  textBlocks.value = extractTextBlocksFromHtml(updatedHtml)
  return true
}

function buildEditorSrcdoc(mode) {
  if (mode === 'paged') return buildPagedPreviewSource(originalHtml.value)
  return buildIframeSource(originalHtml.value)
}

function openEditor(mode = 'normal') {
  if (!originalHtml.value.trim()) return
  error.value = ''
  clearEditorUndoStack()
  editorMode.value = mode
  editorFrameSrcdoc.value = buildEditorSrcdoc(mode)
  resetPaletteSelection()
  resetImageForm()
  domTreeNodes.value = []
  expandedTreeKeys.value = {}
  structuralBodyHtml.value = ''
}

function closeEditor() {
  if (editorMode.value && editorMode.value !== 'paged') syncHtmlFromIframe()
  detachFrameHandlers()
  clearEditorUndoStack()
  resetPaletteSelection()
  editorMode.value = null
  editorFrameSrcdoc.value = ''
  domTreeNodes.value = []
  selectedTreeKey.value = ''
  expandedTreeKeys.value = {}
  structuralBodyHtml.value = ''
}

function replaceFile() {
  closeEditor()
  selectedFile.value = null
  originalHtml.value = ''
  initialHtml.value = ''
  textBlocks.value = []
  fileInput.value?.click()
}

function switchEditorMode(nextMode) {
  if (!nextMode || nextMode === editorMode.value) return
  if (editorMode.value !== 'paged') {
    syncHtmlFromIframe()
  }
  clearEditorUndoStack()
  resetPaletteSelection()
  editorMode.value = nextMode
  editorFrameSrcdoc.value = buildEditorSrcdoc(nextMode)
  domTreeNodes.value = []
  expandedTreeKeys.value = {}
}

async function onFileChange(e) {
  const files = e.target.files
  const f = pickHtmlFile(files)
  if (!f) return
  error.value = ''
  try {
    await loadHtmlFromFile(f)
  } catch (e) {
    selectedFile.value = null
    originalHtml.value = ''
    initialHtml.value = ''
    textBlocks.value = []
    editorMode.value = null
    error.value = e.message || 'Не удалось загрузить HTML'
  }
}

async function onDrop(e) {
  const f = pickHtmlFile(e.dataTransfer.files)
  if (!f) return
  error.value = ''
  try {
    await loadHtmlFromFile(f)
  } catch (e) {
    selectedFile.value = null
    originalHtml.value = ''
    initialHtml.value = ''
    textBlocks.value = []
    editorMode.value = null
    error.value = e.message || 'Не удалось загрузить HTML'
  }
}

function resolvePickTarget(target) {
  if (!target) return null
  let el = target.nodeType === 3 ? target.parentElement : target
  if (!el || el.nodeType !== 1) return null
  const doc = el.ownerDocument
  let tagged = el.closest?.('[data-ncht-node-id]')
  if (!tagged && doc?.body) {
    ensureLiveDocumentNodeIds(doc)
    tagged = el.closest?.('[data-ncht-node-id]')
  }
  if (!tagged) return null
  const tag = String(tagged.tagName || '').toLowerCase()
  if (tag === 'html' || tag === 'body') return null
  return tagged
}

let treeRebuildTimer = null

function scheduleDomTreeRebuild() {
  if (editorMode.value === 'paged') return
  if (treeRebuildTimer) window.clearTimeout(treeRebuildTimer)
  treeRebuildTimer = window.setTimeout(() => {
    treeRebuildTimer = null
    rebuildDomTree(true)
  }, 300)
}

async function onEditorFrameLoad(iframeFromChild) {
  await nextTick()
  const iframe = iframeFromChild || getEditorFrameRef()
  const doc = iframe?.contentDocument
  if (!doc || !iframe) return
  captureStructuralState(doc)
  if (editorMode.value === 'paged') paginatePreviewFrameDocument(doc)
  ensureLiveDocumentNodeIds(doc)
  markEditableInFrameDocument(doc)
  rebuildDomTree(true)
}

onUnmounted(() => {
  detachFrameHandlers()
})

function repaginatePreviewEditor() {
  const doc = getEditorDoc()
  if (!doc) return
  const prevNodeId = selectedTreeKey.value || selectedEditorElement?.getAttribute?.('data-ncht-node-id') || ''
  capturePagedPreviewStructuralState(doc)
  paginatePreviewFrameDocument(doc)
  ensureLiveDocumentNodeIds(doc)
  markEditableInFrameDocument(doc)
  rebuildDomTree(true)
  if (prevNodeId) {
    selectElementByNodeId(prevNodeId)
  }
  ensureFrameHandlersAttached()
}

function onDragOver(e) {
  e.preventDefault()
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result || ''))
    r.onerror = () => reject(new Error('Не удалось прочитать файл'))
    r.readAsText(file, 'UTF-8')
  })
}

async function submit() {
  error.value = ''
  if (!userStore.token) {
    error.value = 'Нужна авторизация'
    return
  }
  if (!selectedFile.value) {
    error.value = 'Выберите HTML-файл'
    return
  }

  if (!originalHtml.value.trim()) {
    error.value = 'Исходный HTML не загружен'
    return
  }
  if (!textBlocks.value.length) {
    error.value = 'Нет текстовых блоков для отправки'
    return
  }
  if (editorMode.value && editorMode.value !== 'paged') syncHtmlFromIframe()
  const html = originalHtml.value
  const htmlBytes = utf8ByteLength(html)
  if (htmlBytes > MAX_HTML_UPLOAD_BYTES) {
    error.value =
      `HTML слишком большой (${formatBytes(htmlBytes)}). Лимит ${formatBytes(MAX_HTML_UPLOAD_BYTES)}. ` +
      'Уберите встроенные изображения (data:image) или вставляйте картинки по URL.'
    return
  }

  const pdfFilename = pdfFilenameFromHtmlName(selectedFile.value.name)

  loading.value = true
  try {
    const form = new FormData()
    form.append('html', new Blob([html], { type: 'text/html;charset=utf-8' }), 'document.html')
    form.append('filename', pdfFilename)

    const res = await fetch(`${apiBase}/api/documents/pdf/from-html`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${userStore.token}`,
      },
      body: form,
    })

    const ct = res.headers.get('content-type') || ''

    if (res.ok && ct.includes('application/pdf')) {
      const blob = await res.blob()
      triggerDownloadBlob(blob, pdfFilename)
      if (html !== initialHtml.value) {
        const editedHtmlBlob = new Blob([html], { type: 'text/html;charset=utf-8' })
        triggerDownloadBlob(editedHtmlBlob, editedHtmlFilenameFromHtmlName(selectedFile.value.name))
      }
    } else {
      let msg = `Ошибка ${res.status}`
      try {
        const j = await res.json()
        if (j.details) msg = j.details
        else if (j.error) msg = j.error
        else if (j.message) msg = j.message
      } catch {
        try {
          const t = await res.text()
          if (t) msg = t.slice(0, 500)
        } catch {
          // ignore
        }
      }
      error.value = msg
    }
  } catch (e) {
    error.value = e.message || 'Сеть или чтение файла'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <HeaderPanel />
  <div class="page-wrap" :class="{ 'page-wrap--editor': isEditorOpen }">
    <template v-if="!isEditorOpen">
      <h1 class="title">HTML → PDF</h1>
      <p class="hint">
        Перетащите .html сюда или выберите файл — откроется редактор с деревом структуры и CSS-панелью.
      </p>

      <div
        class="dropzone"
        @drop.prevent="onDrop"
        @dragover="onDragOver"
        @dragenter.prevent
      >
        <p class="dropzone-title">Перетащите HTML сюда</p>
        <p class="dropzone-subtitle">или нажмите, чтобы выбрать</p>
        <ButtonVue
          label="Выбрать файл"
          class="choose-btn"
          type="button"
          @click="fileInput?.click()"
        />
        <input
          ref="fileInput"
          type="file"
          class="hidden-input"
          accept=".html,.htm,text/html"
          @change="onFileChange"
        />
        <p class="file-info">
          {{ fileName }}
          <span v-if="fileSizeHuman">({{ fileSizeHuman }})</span>
        </p>
      </div>

      <div v-if="selectedFile && !hasEditableBlocks" class="editor-wrap">
        <div class="blocks-summary blocks-summary--warn">
          В этом HTML нет редактируемого текстового контента.
        </div>
      </div>

      <div v-if="error" class="status status-error">
        {{ error }}
      </div>
    </template>

    <HtmlEditorWorkbench
      v-else
      ref="editorWorkbenchRef"
      :file-name="fileName"
      :file-size-human="fileSizeHuman"
      :editor-mode="editorMode"
      :mode-options="editorModeOptions"
      :loading="loading"
      :text-blocks-count="textBlocks.length"
      :can-undo="canUndo"
      :editor-frame-srcdoc="editorFrameSrcdoc"
      :dom-tree-nodes="domTreeNodes"
      :selected-tree-key="selectedTreeKey"
      :expanded-tree-keys="expandedTreeKeys"
      :selected-element-label="selectedElementLabel"
      :width-px="widthPx"
      :height-px="heightPx"
      :font-size-px="fontSizePx"
      :opacity-percent="opacityPercent"
      :border-radius="borderRadius"
      :background-color="backgroundColor"
      :text-color="textColor"
      :text-align-mode="textAlignMode"
      :element-align-mode="elementAlignMode"
      :border-style="borderStyle"
      :margin-top="marginTop"
      :margin-right="marginRight"
      :margin-bottom="marginBottom"
      :margin-left="marginLeft"
      :padding-top="paddingTop"
      :padding-right="paddingRight"
      :padding-bottom="paddingBottom"
      :padding-left="paddingLeft"
      :border-top-width="borderTopWidth"
      :border-right-width="borderRightWidth"
      :border-bottom-width="borderBottomWidth"
      :border-left-width="borderLeftWidth"
      :border-top-color="borderTopColor"
      :border-right-color="borderRightColor"
      :border-bottom-color="borderBottomColor"
      :border-left-color="borderLeftColor"
      :format-context-menu-items="formatContextMenuItems"
      :error="error"
      @update:editor-mode="switchEditorMode"
      @replace-file="replaceFile"
      @close-editor="closeEditor"
      @download-pdf="submit"
      @repaginate="repaginatePreviewEditor"
      @frame-load="onEditorFrameLoad($event)"
      @tree-select="onTreeSelect"
      @tree-node-drop="onTreeNodeDrop"
      @tree-context-action="onTreeContextAction"
      @undo="handleUndo"
      @update:expanded-tree-keys="expandedTreeKeys = $event"
      @update:width-px="widthPx = $event"
      @update:height-px="heightPx = $event"
      @update:font-size-px="fontSizePx = $event"
      @update:opacity-percent="opacityPercent = $event"
      @update:border-radius="borderRadius = $event"
      @update:background-color="backgroundColor = $event"
      @update:text-color="textColor = $event"
      @update:border-style="borderStyle = $event"
      @update:margin-top="marginTop = $event"
      @update:margin-right="marginRight = $event"
      @update:margin-bottom="marginBottom = $event"
      @update:margin-left="marginLeft = $event"
      @update:padding-top="paddingTop = $event"
      @update:padding-right="paddingRight = $event"
      @update:padding-bottom="paddingBottom = $event"
      @update:padding-left="paddingLeft = $event"
      @update:border-top-width="borderTopWidth = $event"
      @update:border-right-width="borderRightWidth = $event"
      @update:border-bottom-width="borderBottomWidth = $event"
      @update:border-left-width="borderLeftWidth = $event"
      @update:border-top-color="borderTopColor = $event"
      @update:border-right-color="borderRightColor = $event"
      @update:border-bottom-color="borderBottomColor = $event"
      @update:border-left-color="borderLeftColor = $event"
      @apply-layout="applyLayoutStyles"
      @apply-spacing="applySpacingStyles"
      @apply-appearance="applyAppearanceStyles"
      @set-element-align="setElementAlignMode"
      @set-text-align="setTextAlignMode"
      @remove-element="removeSelectedElement"
      @reset-selection="resetPaletteSelection"
      @image-file-change="onImageFileChange"
    />

    <input
      v-if="isEditorOpen"
      ref="fileInput"
      type="file"
      class="hidden-input"
      accept=".html,.htm,text/html"
      @change="onFileChange"
    />
  </div>
</template>

<style scoped>
.page-wrap {
  max-width: 520px;
  margin: 0 auto;
  padding: 24px 16px 32px;
  background: var(--surface-ground, #f9fafb);
}

.page-wrap--editor {
  max-width: none;
  margin: 0;
  padding: 0;
  background: transparent;
}

.title {
  margin-bottom: 8px;
  font-size: 24px;
  font-weight: 600;
  color: var(--text-color, #111827);
}

.hint {
  margin-bottom: 20px;
  font-size: 14px;
  color: var(--text-color-secondary, #6b7280);
  line-height: 1.5;
}

.dropzone {
  border: 2px dashed var(--surface-border, #e5e7eb);
  border-radius: 12px;
  padding: 24px 16px;
  text-align: center;
  margin-bottom: 16px;
  background: var(--surface-card, #ffffff);
}

.dropzone-title {
  font-weight: 500;
  margin-bottom: 4px;
  color: var(--text-color, #111827);
}

.dropzone-subtitle {
  margin-bottom: 12px;
  color: var(--text-color-secondary, #6b7280);
  font-size: 14px;
}

.choose-btn {
  margin-bottom: 12px;
}

.hidden-input {
  display: none;
}

.file-info {
  font-size: 14px;
  color: var(--text-color-secondary, #4b5563);
  word-break: break-all;
}

.actions {
  margin-top: 8px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.editor-wrap {
  margin-bottom: 16px;
}

.editor-head {
  margin-bottom: 8px;
}

.editor-label {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-color, #111827);
}

.blocks-summary {
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 10px;
  padding: 12px;
}

.blocks-summary--warn {
  color: var(--p-yellow-800, #92400e);
  background: var(--p-yellow-50, #fffbeb);
  border-color: var(--p-yellow-200, #fde68a);
}

.hidden-input {
  display: none;
}

.status-error {
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: var(--p-red-50, #fef2f2);
  color: var(--p-red-700, #b91c1c);
  border: 1px solid var(--p-red-200, #fecaca);
  font-size: 14px;
}
</style>
